import React, { FunctionComponent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { NamedProperty } from "sprint-common-web";

import { VersionType } from "../../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { GetMovieByIdResponse } from "../../api/GetMovieByIdResponse";
import { useAssetsManageMovieApi } from "../../api/useAssetsManageMovieApi";
import { MovieDetails, MovieVersionDetails } from "../MovieDetails";
import { MovieContext } from "./MovieContext";

type MovieProviderProps = React.PropsWithChildren<unknown>;

export const MovieProvider: FunctionComponent<MovieProviderProps> = (props: MovieProviderProps) => {
    const { children } = props;

    const { movieId } = useParams<{ movieId: string }>();

    const { getMovieById } = useAssetsManageMovieApi();

    const [movie, setMovie] = useState<MovieDetails>({ id: "", title: "", versions: [] });

    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        getMovie(movieId);
    }, [movieId]);

    const getMovie = (id: string) => {
        setIsLoading(true);

        getMovieById(id)
            .then((response: GetMovieByIdResponse) => {
                const newMovie = response.item;

                setMovie(newMovie);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const setVersionAsMerged = (versionId: string, mergedTargetId: string) => {
        setMovie((prevMovie: MovieDetails | undefined) => {
            if (!prevMovie) throw new Error("To set version as merged Movie must be defined");

            const version = prevMovie.versions.find(
                (movieVersion: MovieVersionDetails) => movieVersion.id === versionId
            );
            if (!version) throw new Error("No Movie Version with given id");

            version.mergedTargetId = mergedTargetId;

            return { ...prevMovie };
        });
    };

    const updateMovieVersion = (versionId: string, distirbutor: NamedProperty, type: VersionType) => {
        const versionToUpdate = movie.versions.find((version: MovieVersionDetails) => version.id === versionId);
        if (versionToUpdate) {
            versionToUpdate.distributor = distirbutor;
            versionToUpdate.type = type;
        }

        setMovie(movie);
    };

    const setMovieDetails = (title: string, year?: number) => {
        setMovie({ ...movie, title, year });
    };

    const addMovieVersion = (newMovieVersion: MovieVersionDetails) => {
        setMovie({ ...movie, versions: [...movie.versions, newMovieVersion] });
    };

    return (
        <MovieContext.Provider
            value={{ movie, addMovieVersion, isLoading, setMovieDetails, updateMovieVersion, setVersionAsMerged }}
        >
            {children}
        </MovieContext.Provider>
    );
};
